import {
  forwardRef,
  type MouseEvent,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Bar,
  type BarRectangleItem,
  CartesianGrid,
  Label,
  BarChart as RechartsBarChart,
  type BarShapeProps as RechartsBarShapeProps,
  Legend as RechartsLegend,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  type XAxisProps,
  YAxis,
} from "recharts";
import type { Payload as TooltipPayload } from "recharts/types/component/DefaultTooltipContent";
import type { AxisDomain } from "recharts/types/util/types";
import { cn } from "@/lib/utils";
import {
  AvailableChartColors,
  type AvailableChartColorsKeys,
  type AvailableChartGradientsKeys,
  chartGradientColors,
  constructCategoryColors,
  getActiveBarSyncKey,
  getActiveClass,
  getChartRowField,
  getGradientId,
  getInactiveClass,
  getYAxisDomain,
  getYAxisDomainForSeriesData,
} from "@/lib/utils/chartUtils.ts";

import type {
  BarChartProps,
  BarShapeProps,
  PayloadItem,
  ShapeProps,
} from "./BarChart.types";
import { ChartLegend } from "./ChartLegend";
import ChartTooltip from "./ChartTooltip";

type ValueType = string | number | Array<string | number>;
type NameType = string | number;

/** Recharts 3 ResponsiveContainer: avoids width/height -1 warnings before first layout measure. */
const BAR_CHART_RESPONSIVE_INITIAL = { width: 320, height: 200 } as const;

/** Shared by X/Y axis ticks so styling matches across rotated vs default horizontal X ticks. */
const AXIS_TICK_CLASS = "fill-gray-500 text-[9px] dark:fill-gray-300";

const BarShape = memo(function Shape<T extends PayloadItem>({
  props,
  activeBar,
  activeLegend,
  layout,
  mode,
  colorKey,
  borderRadius = 0,
  indexKey,
}: BarShapeProps<T>): React.ReactElement {
  const { name, payload, value, x = 0, y = 0, width = 0, height = 0 } = props;

  const w = typeof width === "number" ? width : Number(width);
  const h = typeof height === "number" ? height : Number(height);

  let correctedX = Number.parseInt(x.toString(), 10);
  let correctedY = Number.parseInt(y.toString(), 10);
  let correctedWidth = Number.isFinite(w) ? w : 0;
  let correctedHeight = Number.isFinite(h) ? h : 0;

  if (layout === "horizontal" && h < 0) {
    correctedY += h;
    correctedHeight = Math.abs(h);
  } else if (layout === "vertical" && w < 0) {
    correctedX += w;
    correctedWidth = Math.abs(w);
  }

  const fill =
    mode === "gradient" ? `url(#${getGradientId(colorKey)})` : undefined;

  const isInactive = useMemo(() => {
    if (!activeBar && activeLegend && name) {
      return activeLegend !== name;
    }
    if (!activeBar) {
      return false;
    }
    const selectedSeries = activeLegend ?? activeBar.category;
    if (selectedSeries === undefined || !name) {
      return false;
    }
    const rowId = getChartRowField(payload, indexKey);
    const activeRowId = activeBar
      ? getChartRowField(activeBar, indexKey)
      : undefined;
    const sameRow = rowId === activeRowId;
    const sameSeries = selectedSeries === name;
    return !(sameRow && sameSeries);
  }, [activeBar, activeLegend, name, payload, indexKey]);

  return (
    <rect
      data-chart-bar-segment=""
      className={cn(
        "transform-gpu will-change-[fill,opacity] transition-opacity duration-200",
        {
          [getActiveClass(colorKey)]: !isInactive && mode === "solid",
          [getInactiveClass(colorKey)]: isInactive,
        },
      )}
      x={correctedX}
      y={correctedY}
      width={correctedWidth}
      height={correctedHeight}
      fill={fill}
      rx={borderRadius}
      ry={borderRadius}
      stroke={value === 0 ? "none" : "white"}
      strokeWidth={value === 0 ? 0 : 1}
    />
  );
});

BarShape.displayName = "BarShape";

const RotatedXAxisTick = memo(
  ({
    x,
    y,
    payload,
    formatXAxisTick,
    index,
  }: {
    x: number | string;
    y: number | string;
    payload: {
      value: string | number;
      coordinate: number;
      index: number;
    };
    formatXAxisTick?: XAxisProps["tickFormatter"];
    index: number;
  }) => (
    <text
      x={x}
      y={y}
      dy={10}
      textAnchor="end"
      transform={`rotate(-45, ${x}, ${y})`}
      className={AXIS_TICK_CLASS}
    >
      {formatXAxisTick ? formatXAxisTick(payload.value, index) : payload.value}
    </text>
  ),
);

/** Horizontal layout, non-rotated ticks — same typography/colour as {@link RotatedXAxisTick} (default Recharts ticks did not match). */
const DefaultHorizontalXAxisTick = memo(
  ({
    x,
    y,
    payload,
    formatXAxisTick,
    index,
  }: {
    x: number | string;
    y: number | string;
    payload: {
      value: string | number;
      coordinate: number;
      index: number;
    };
    formatXAxisTick?: XAxisProps["tickFormatter"];
    index: number;
  }) => (
    <Text
      x={x}
      y={y}
      dy={6}
      textAnchor="middle"
      verticalAnchor="start"
      className={AXIS_TICK_CLASS}
    >
      {formatXAxisTick ? formatXAxisTick(payload.value, index) : payload.value}
    </Text>
  ),
);

DefaultHorizontalXAxisTick.displayName = "DefaultHorizontalXAxisTick";

// Custom Y-axis tick component that measures its width
const MeasurableYAxisTick = memo(
  ({
    x,
    y,
    payload,
    valueFormatter,
    onWidthChange,
  }: {
    x: number | string;
    y: number | string;
    payload: {
      value: string | number;
      coordinate: number;
      index: number;
    };
    valueFormatter?: (value: number) => string;
    onWidthChange?: (width: number) => void;
  }) => {
    const textRef = useRef<SVGTextElement>(null);

    useLayoutEffect(() => {
      if (textRef.current && onWidthChange) {
        const width = textRef.current.getBBox().width;
        onWidthChange(width);
      }
    }, [payload.value, onWidthChange]);

    return (
      <Text
        x={x}
        y={y}
        textAnchor="end"
        verticalAnchor="middle"
        className={AXIS_TICK_CLASS}
        ref={textRef}
      >
        {valueFormatter && typeof payload.value === "number"
          ? valueFormatter(payload.value)
          : payload.value}
      </Text>
    );
  },
);

MeasurableYAxisTick.displayName = "MeasurableYAxisTick";

RotatedXAxisTick.displayName = "RotatedXAxisTick";

const BarChart = memo(
  forwardRef<HTMLDivElement, BarChartProps<PayloadItem>>(
    (props, forwardedRef) => {
      const {
        data = [],
        categories = [],
        stackedBy,
        index,
        colors = AvailableChartColors,
        valueFormatter = (value: number) => value.toString(),
        startEndOnly = false,
        showXAxis = true,
        showYAxis = true,
        showGridLines = true,
        yAxisWidth = 56,
        intervalType = "equidistantPreserveStart",
        showTooltip = true,
        showLegend = true,
        autoMinValue = true,
        minValue,
        maxValue,
        allowDecimals = true,
        className,
        onValueChange,
        enableLegendSlider = false,
        barCategoryGap,
        tickGap = 5,
        xAxisLabel,
        yAxisLabel,
        layout = "horizontal",
        type = "default",
        tooltipCallback,
        customTooltip,
        borderRadius,
        formatXAxisTick,
        rotateXAxisTick,
        mode = "solid",
        defaultActiveBar,
        ...other
      } = props;

      const [legendHeight, setLegendHeight] = useState(60);
      const [calculatedYAxisWidth, setCalculatedYAxisWidth] =
        useState(yAxisWidth);
      const [maxYAxisLabelWidth, setMaxYAxisLabelWidth] = useState(0);
      const [activeLegend, setActiveLegend] = useState<string | undefined>(
        undefined,
      );
      const [activeBar, setActiveBar] = useState<PayloadItem | undefined>(
        defaultActiveBar,
      );
      const chartContainerRef = useRef<HTMLDivElement>(null);
      const isFirstRender = useRef(true);
      const prevDefaultActiveBarKeyRef = useRef<string | null>(null);

      const CustomTooltip = customTooltip;
      const paddingValue =
        (!showXAxis && !showYAxis) || (startEndOnly && !showYAxis) ? 0 : 20;
      const categoryColors = useMemo(
        () => constructCategoryColors(categories, colors),
        [categories, colors],
      );
      const yAxisDomain = useMemo(
        () =>
          minValue != null || maxValue != null
            ? getYAxisDomain(autoMinValue, minValue, maxValue)
            : getYAxisDomainForSeriesData(data, categories, { autoMinValue }),
        [autoMinValue, minValue, maxValue, data, categories],
      );
      const hasOnValueChange = !!onValueChange;
      const stacked = type === "stacked" || type === "percent";

      // Update Y-axis width based on measured label widths
      useEffect(() => {
        if (maxYAxisLabelWidth > 0) {
          // Add padding to the measured width
          const newWidth = Math.max(maxYAxisLabelWidth + 16, 40);
          setCalculatedYAxisWidth(newWidth);
        }
      }, [maxYAxisLabelWidth]);

      // Handle Y-axis label width measurement
      const handleYAxisLabelWidthChange = useCallback((width: number) => {
        setMaxYAxisLabelWidth((prev) => Math.max(prev, width));
      }, []);

      useEffect(() => {
        const key = getActiveBarSyncKey(defaultActiveBar, index);
        if (prevDefaultActiveBarKeyRef.current === key) {
          return;
        }
        prevDefaultActiveBarKeyRef.current = key;
        setActiveBar(defaultActiveBar);
        setActiveLegend(defaultActiveBar?.category);
      }, [defaultActiveBar, index]);

      // Reset max width when data changes
      useEffect(() => {
        setMaxYAxisLabelWidth(0);
      }, [data]);

      // Add animation for initial render
      useEffect(() => {
        if (isFirstRender.current && chartContainerRef.current) {
          chartContainerRef.current.style.opacity = "0";

          // Delay to ensure DOM is ready
          const timer = setTimeout(() => {
            if (chartContainerRef.current) {
              chartContainerRef.current.style.opacity = "1";
              isFirstRender.current = false;
            }
          }, 50);

          return () => clearTimeout(timer);
        }
      }, []);

      const valueToPercent = useCallback((value: number) => {
        return `${(value * 100).toFixed(0)}%`;
      }, []);

      /** Vertical bar layout: X-axis is numeric — match {@link DefaultHorizontalXAxisTick} styling. */
      const renderVerticalLayoutNumericXAxisTick = useCallback(
        (props: {
          x: number | string;
          y: number | string;
          payload: {
            value: string | number;
            coordinate: number;
            index: number;
          };
        }) => {
          const raw = props.payload.value;
          const num = typeof raw === "number" ? raw : Number(raw);
          const safe = Number.isFinite(num) ? num : 0;
          const label =
            type === "percent" ? valueToPercent(safe) : valueFormatter(safe);
          return (
            <Text
              x={props.x}
              y={props.y}
              dy={6}
              textAnchor="middle"
              verticalAnchor="start"
              className={AXIS_TICK_CLASS}
            >
              {label}
            </Text>
          );
        },
        [type, valueFormatter, valueToPercent],
      );

      const onBarClick = useCallback(
        (
          data: BarRectangleItem,
          _index: number,
          event: React.MouseEvent<SVGPathElement>,
          category: string,
        ) => {
          event.stopPropagation();
          if (!onValueChange) return;

          const rowPayload = data.payload;
          if (rowPayload == null || typeof rowPayload !== "object") {
            return;
          }

          const numericValue = Array.isArray(data.value)
            ? data.value[1]
            : data.value;

          const rowId = getChartRowField(rowPayload, index);
          const activeRowId = activeBar
            ? getChartRowField(activeBar, index)
            : undefined;
          const isSameSegment =
            activeBar &&
            activeBar.category === category &&
            rowId === activeRowId;

          if (isSameSegment) {
            setActiveLegend(undefined);
            setActiveBar(undefined);
            onValueChange?.(null);
          } else {
            setActiveLegend(category);
            setActiveBar({
              ...rowPayload,
              value: numericValue,
              category,
            } as PayloadItem);
            onValueChange?.({
              eventType: "bar",
              categoryClicked: category,
              ...rowPayload,
              value: numericValue,
              // biome-ignore lint/suspicious/noExplicitAny: chart row shape is wider than BaseEventProps
            } as any);
          }
        },
        [activeBar, index, onValueChange],
      );

      const handleChartClick = useCallback(
        (_nextState: unknown, event: MouseEvent<SVGGraphicsElement>) => {
          const el = event.target;
          // Bar onClick calls stopPropagation, but Recharts can still invoke this handler — skip bar targets.
          if (el instanceof Element && el.closest("[data-chart-bar-segment]")) {
            return;
          }
          if (hasOnValueChange && (activeLegend || activeBar)) {
            setActiveBar(undefined);
            setActiveLegend(undefined);
            onValueChange?.(null);
          }
        },
        [hasOnValueChange, activeLegend, activeBar, onValueChange],
      );

      /**
       * Chrome focuses the chart SVG on mousedown, which shows a focus ring until blur; other
       * browsers typically do not. preventDefault keeps the ring off without affecting click.
       * Skip when the target is the portaled legend so focusable legend items still work.
       */
      const suppressChromeSvgFocusOnMouseDown = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
          const target = e.target;
          if (!(target instanceof Element)) return;
          if (target.closest(".recharts-legend-wrapper")) return;
          if (target.closest(".recharts-surface")) {
            e.preventDefault();
          }
        },
        [],
      );

      return (
        <div
          ref={forwardedRef}
          className={cn(
            "h-80 w-full min-w-0 transform-gpu will-change-transform",
            "transition-all duration-300 ease-in-out",
            className,
          )}
          tremor-id="tremor-raw"
          {...other}
        >
          {/* biome-ignore lint/a11y/noStaticElementInteractions: suppresses Chrome SVG focus ring on chart mousedown; not a primary control */}
          <div
            ref={chartContainerRef}
            className="h-full min-h-0 w-full min-w-0 transition-opacity duration-500 ease-in-out [&_.recharts-surface]:outline-none [&_.recharts-surface]:focus:outline-none [&_.recharts-surface]:focus-visible:outline-none [&_.recharts-surface]:focus:ring-0 [&_.recharts-surface]:focus-visible:ring-0"
            onMouseDown={suppressChromeSvgFocusOnMouseDown}
          >
            <ResponsiveContainer
              height="100%"
              initialDimension={BAR_CHART_RESPONSIVE_INITIAL}
              minHeight={200}
              minWidth={0}
              width="100%"
            >
              <RechartsBarChart
                data={data}
                onClick={
                  hasOnValueChange && (activeLegend || activeBar)
                    ? handleChartClick
                    : undefined
                }
                margin={{
                  bottom: xAxisLabel ? 30 : rotateXAxisTick ? 40 : 20,
                  left: yAxisLabel ? 20 : 0,
                  right: 10,
                  top: 10,
                }}
                stackOffset={type === "percent" ? "expand" : undefined}
                layout={layout}
                barCategoryGap={barCategoryGap}
                className="transition-all duration-300 ease-in-out"
              >
                {mode === "gradient" && (
                  <defs>
                    {categories.map((category) => (
                      <linearGradient
                        key={category}
                        id={getGradientId(
                          categoryColors.get(
                            category,
                          ) as AvailableChartColorsKeys,
                        )}
                        gradientTransform={"rotate(90)"}
                      >
                        {chartGradientColors[
                          categoryColors.get(
                            category,
                          ) as AvailableChartGradientsKeys
                        ]?.stops.map((stop, index) => (
                          <stop
                            // biome-ignore lint/suspicious/noArrayIndexKey: <legacy>
                            key={index}
                            offset={stop.offset}
                            stopColor={stop.color}
                            stopOpacity="1"
                          />
                        ))}
                      </linearGradient>
                    ))}
                  </defs>
                )}

                {showGridLines ? (
                  <CartesianGrid
                    className={cn(
                      "stroke-gray-200 stroke-1 dark:stroke-gray-800",
                    )}
                    horizontal={layout !== "vertical"}
                    vertical={layout === "vertical"}
                  />
                ) : null}
                <XAxis
                  hide={!showXAxis}
                  tick={
                    rotateXAxisTick
                      ? (props) => (
                          <RotatedXAxisTick
                            {...props}
                            formatXAxisTick={formatXAxisTick}
                          />
                        )
                      : layout !== "vertical"
                        ? (props) => (
                            <DefaultHorizontalXAxisTick
                              {...props}
                              formatXAxisTick={formatXAxisTick}
                            />
                          )
                        : renderVerticalLayoutNumericXAxisTick
                  }
                  fill=""
                  stroke=""
                  className={cn(AXIS_TICK_CLASS, {
                    "mt-4": layout !== "vertical",
                  })}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={tickGap}
                  tickFormatter={formatXAxisTick}
                  {...(layout !== "vertical"
                    ? {
                        padding: {
                          left: paddingValue,
                          right: paddingValue,
                        },
                        dataKey: index,
                        interval: startEndOnly
                          ? "preserveStartEnd"
                          : intervalType,
                        ticks: startEndOnly
                          ? //@ts-ignore
                            [data[0][index], data[data.length - 1][index]]
                          : undefined,
                      }
                    : {
                        type: "number",
                        domain: yAxisDomain as AxisDomain,
                        tickFormatter:
                          type === "percent" ? valueToPercent : valueFormatter,
                        allowDecimals: allowDecimals,
                      })}
                >
                  {xAxisLabel && (
                    <Label
                      position="insideBottom"
                      offset={-20}
                      className="fill-gray-800 text-sm font-medium dark:fill-gray-200"
                    >
                      {xAxisLabel}
                    </Label>
                  )}
                </XAxis>
                <YAxis
                  width={calculatedYAxisWidth}
                  hide={!showYAxis}
                  axisLine={false}
                  tickLine={false}
                  fill=""
                  stroke=""
                  className={cn(
                    "text-[9px]",
                    "fill-gray-500 dark:fill-gray-500",
                  )}
                  tick={(props) => (
                    <MeasurableYAxisTick
                      {...props}
                      valueFormatter={
                        type === "percent" ? valueToPercent : valueFormatter
                      }
                      onWidthChange={handleYAxisLabelWidthChange}
                    />
                  )}
                  {...(layout !== "vertical"
                    ? {
                        type: "number",
                        domain: yAxisDomain as AxisDomain,
                        tickFormatter:
                          type === "percent" ? valueToPercent : valueFormatter,
                        allowDecimals: allowDecimals,
                      }
                    : {
                        dataKey: index,
                        ticks: startEndOnly
                          ? //@ts-ignore
                            [data[0][index], data[data.length - 1][index]]
                          : undefined,
                        type: "category",
                        interval: "equidistantPreserveStart",
                      })}
                >
                  {yAxisLabel && (
                    <Label
                      position="insideLeft"
                      style={{ textAnchor: "middle" }}
                      angle={-90}
                      offset={-15}
                      className="fill-gray-800 text-sm font-medium dark:fill-gray-200"
                    >
                      {yAxisLabel}
                    </Label>
                  )}
                </YAxis>
                <Tooltip
                  shared={false}
                  wrapperStyle={{ outline: "none" }}
                  animationDuration={180}
                  wrapperClassName="transition-transform duration-180 ease-out"
                  cursor={{ fill: "transparent", opacity: "0.15" }}
                  offset={20}
                  content={({ active, payload, label }) => {
                    // Plain computation: Recharts passes a new component each time so useMemo would not memoize across renders.
                    const cleanPayload = (() => {
                      // Use hover payload only when Recharts reports active (cursor over chart).
                      // Otherwise after click we can get stale payload; only show selection when !active.
                      if (active && payload && payload.length > 0) {
                        // Normal hover behavior - use Recharts payload directly
                        const seen = new Set<string>();
                        const result: PayloadItem[] = [];

                        for (const item of payload) {
                          const tooltipItem = item as TooltipPayload<
                            ValueType,
                            NameType
                          > & {
                            dataKey: string;
                            value: number;
                            payload: PayloadItem;
                          };

                          if (seen.has(tooltipItem.dataKey)) continue;

                          const color = categoryColors.get(tooltipItem.dataKey);
                          if (!color) continue;

                          seen.add(tooltipItem.dataKey);
                          result.push({
                            category: tooltipItem.dataKey,
                            value: tooltipItem.value,
                            index: tooltipItem.payload[index] as string,
                            color: color as string,
                            type: item.type,
                            payload: item.payload,
                            id: data.indexOf(item.payload),
                          });
                        }

                        // Stacked bars: Recharts often sends all segments; show only the first
                        // (hovered segment is typically first with shared={false})
                        if (stacked && result.length > 1) {
                          return result.slice(0, 1);
                        }
                        return result;
                      }
                      // No hover payload: if a bar is selected, show its breakdown (all categories)
                      if (!active && activeBar) {
                        const activeRowId = getChartRowField(activeBar, index);
                        const activeDataPoint = data.find(
                          (d) => d[index] === activeRowId,
                        );
                        if (activeDataPoint) {
                          const payloadItems: PayloadItem[] = [];
                          const seen = new Set<string>();

                          for (const category of categories) {
                            if (seen.has(category)) continue;

                            const value = activeDataPoint[category];
                            if (
                              value === undefined ||
                              value === null ||
                              (typeof value === "number" && value <= 0)
                            ) {
                              continue;
                            }

                            const color = categoryColors.get(category);
                            if (!color) continue;

                            seen.add(category);
                            payloadItems.push({
                              category,
                              value: typeof value === "number" ? value : 0,
                              index: activeDataPoint[index] as string,
                              color: color as string,
                              type: "bar",
                              payload: activeDataPoint,
                              id: data.indexOf(activeDataPoint),
                            });
                          }

                          return payloadItems;
                        }
                        return [
                          {
                            category: activeBar.category,
                            value: activeBar.value,
                            index:
                              (getChartRowField(activeBar, index) as string) ??
                              activeBar.index,
                            color: activeBar.color as AvailableChartColorsKeys,
                            type: activeBar.type || "bar",
                            payload: activeBar.payload,
                            id: activeBar.id,
                          },
                        ];
                      }
                      return [];
                    })();

                    // Show tooltip if active (hover) OR if there's an activeBar (clicked)
                    const shouldShowTooltip =
                      showTooltip && (active || activeBar);

                    return shouldShowTooltip && cleanPayload.length > 0 ? (
                      CustomTooltip ? (
                        <CustomTooltip
                          active={active || !!activeBar}
                          payload={cleanPayload}
                          label={label ?? activeBar?.index ?? ""}
                        />
                      ) : (
                        <ChartTooltip
                          active={active || !!activeBar}
                          payload={cleanPayload}
                          label={label ?? activeBar?.index ?? ""}
                          valueFormatter={valueFormatter}
                          mode={mode}
                          formatXAxisTick={formatXAxisTick}
                        />
                      )
                    ) : null;
                  }}
                />
                {showLegend ? (
                  <RechartsLegend
                    verticalAlign="bottom"
                    height={legendHeight}
                    wrapperStyle={{ transform: "translateZ(0)" }}
                    content={({ payload }) => {
                      return (
                        <ChartLegend
                          payload={payload != null ? [...payload] : []}
                          categoryColors={categoryColors}
                          setLegendHeight={setLegendHeight}
                          yAxisWidth={calculatedYAxisWidth}
                          activeLegend={activeLegend}
                          enableLegendSlider={enableLegendSlider}
                          rotateXAxisTick={rotateXAxisTick}
                        />
                      );
                    }}
                  />
                ) : null}
                {categories.map((category) => {
                  const colorKey = categoryColors.get(
                    category,
                  ) as AvailableChartColorsKeys;
                  const activeClass = getActiveClass(colorKey);
                  const inactiveClass = getInactiveClass(colorKey);
                  const selectedSeries = activeLegend ?? activeBar?.category;

                  return (
                    <Bar
                      className={cn(
                        "transform-gpu will-change-[transform,opacity] transition-[opacity,transform] duration-300 ease-out",
                        activeBar ? inactiveClass : activeClass,
                        onValueChange ? "cursor-pointer" : "",
                      )}
                      key={category}
                      name={category}
                      type="linear"
                      dataKey={category}
                      stackId={stacked ? "stack" : undefined}
                      isAnimationActive={true}
                      animationDuration={200}
                      style={{
                        transitionDelay: activeBar
                          ? `${categories.indexOf(category) * 50}ms`
                          : "0ms",
                        opacity:
                          activeBar &&
                          selectedSeries !== undefined &&
                          selectedSeries !== category
                            ? 0.3
                            : 1,
                      }}
                      shape={(props: RechartsBarShapeProps) => (
                        <BarShape
                          props={props as ShapeProps<PayloadItem>}
                          activeBar={activeBar}
                          activeLegend={activeLegend}
                          layout={layout}
                          mode={mode}
                          colorKey={colorKey}
                          borderRadius={borderRadius}
                          indexKey={index}
                        />
                      )}
                      onClick={(data, index, event) =>
                        onBarClick(data, index, event, category)
                      }
                      maxBarSize={30}
                    />
                  );
                })}
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    },
  ),
);

BarChart.displayName = "BarChart";

export default BarChart;
